'use client';

import React, { useRef, useState, useTransition } from "react";
import { Send } from "lucide-react"
import { Button } from "@kit/ui/button"
import { Input } from "@kit/ui/input"
import { MessageSchema } from "~/lib/schemas/MessageSchema.schema";
import { z } from "zod";
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import { v4 as uuidv4 } from 'uuid';
import { addMessagesAction } from "~/lib/server/messages/addMessageAction";
import { useTranslation } from "react-i18next";
import { Messages } from "./messages";

interface ChatWithInputProps {
  client: {
    id: string;
    name: string;
    logo_url: string | null;
    page: string;
    expired_at: string
  };
  history: z.infer<typeof MessageSchema>[];
  sessionId: string;
  initialMessage?: z.infer<typeof MessageSchema> | null;
  isClientOffline?: boolean;
}

// TODO: Tidy up contents UI renderer, separate that
export function ChatWithInput({ client, sessionId, history, initialMessage, isClientOffline }: ChatWithInputProps) {
  const { t } = useTranslation('teams');
  const [isPending, startTransition] = useTransition();
  const [input, setInput] = useState('');
  const initialMessages = [
    ...(history.length === 0 && initialMessage ? [initialMessage] : []),
    ...(history ?? []).map(message => MessageSchema.parse(message))
  ];
  const isUserWasSubmittedAMessage = useRef(false);

  const { messages, sendMessage, error } = useChat({
    transport: new DefaultChatTransport({
      api: '/api/chat',
      body: {
        client_id: client.id,
        session_id: sessionId
      }
    }),
    messages: initialMessages.map(item => {
      return {
        id: item.id ?? `${uuidv4()}`,
        parts: [{ text: item.message ?? '-', type: 'text' as const }],
        role: item.author === 'user' ? 'user' as const : 'assistant' as const,
        metadata: item.content ? item : undefined
      }
    }),
  });

  const shouldWaitUntilResponse = (messages.length > 0 && messages[messages.length - 1]?.role === 'user') && (error == undefined || error == null); // if last message is user and no error
  
  const inputLength = input.trim().length
  
  return (
    <div className='flex-1 flex flex-col overflow-hidden'>
      <Messages messages={messages} error={error} />
      <div className="sticky bottom-0 p-4 bg-muted container w-full max-w-4xl" style={{ borderTopLeftRadius: '0.5rem', borderTopRightRadius: '0.5rem' }}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (isClientOffline || !input.trim()) return;
            
            const userMessage = input.trim();
            setInput(''); // Clear input immediately
            
            startTransition(async () => {
              if (messages.length > 0 && messages[0]?.id && history.length === 0 && initialMessage && !isUserWasSubmittedAMessage.current) {
                await addMessagesAction([
                  {
                    ...initialMessage,
                    id: undefined,
                    author: initialMessage.author ?? 'assistant',
                    client_id: initialMessage.client_id ?? client.id,
                    content_type: initialMessage.content_type ?? 'text',
                    message: initialMessage.message ?? '',
                    session_id: sessionId ?? `${uuidv4()}`
                  }
                ]);
              }
  
              isUserWasSubmittedAMessage.current = true;
              sendMessage({ text: userMessage });
            });
          }}
          className="flex w-full items-center space-x-2"
        >
          <Input
            id="message"
            placeholder={isClientOffline ? t('chatInputPlaceholderDisabled') : t('chatInputPlaceholder')}
            className="flex-1 border-2"
            autoComplete="off"
            value={input}
            disabled={isClientOffline}
            onChange={(e) => setInput(e.target.value)}
          />
          <Button type="submit" size="icon" disabled={inputLength === 0 || isClientOffline || isPending || shouldWaitUntilResponse}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
